/**
 * analytics.ts — thin, key-gated analytics wrapper.
 *
 * Gated entirely behind NEXT_PUBLIC_POSTHOG_KEY. When the key is ABSENT
 * (the current default — the user is provisioning no external accounts),
 * every exported function is a guaranteed NO-OP: no network, no crash, no
 * console noise. When the key is PRESENT, calls forward to posthog-js.
 *
 * Init happens once in <AnalyticsProvider> (client component). These helpers
 * are safe to import from any client component or client hook. They are inert
 * during SSR (posthog-js is browser-only) and inert without a key.
 */

import type { PostHog } from "posthog-js";

/** The six funnel events we care about. Use these constants — never raw strings. */
export const ANALYTICS_EVENTS = {
  LANDING_VIEW: "landing_view",
  SIGNUP: "signup",
  ONBOARDING_COMPLETE: "onboarding_complete",
  FIRST_BATTLE: "first_battle",
  DELEGATION_RUN: "delegation_run",
  CHECKOUT_START: "checkout_start",
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

type Props = Record<string, string | number | boolean | null | undefined>;

/** Read the public key. Empty string / undefined ⇒ analytics disabled. */
export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";

/** Host is optional — defaults to PostHog US cloud when a key is set. */
export const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

/** True only when a key is configured. Everything below short-circuits on this. */
export const analyticsEnabled = (): boolean =>
  POSTHOG_KEY.length > 0 && typeof window !== "undefined";

// Lazily-held client. Populated by initAnalytics(); null when disabled.
let client: PostHog | null = null;

/**
 * Initialize PostHog. Call once, client-side (AnalyticsProvider does this).
 * No-op when the key is absent or when not in a browser. Safe to call twice.
 */
export async function initAnalytics(): Promise<void> {
  if (!analyticsEnabled() || client) return;
  // Dynamic import so posthog-js is never pulled into the server bundle and
  // is only fetched at all when a key exists.
  const { default: posthog } = await import("posthog-js");
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false, // we send landing_view / route events explicitly
    capture_pageleave: true,
    autocapture: false, // explicit funnel events only — keep it intentional
    persistence: "localStorage+cookie",
  });
  client = posthog;
}

/**
 * Track a funnel event. NO-OP without a key. Accepts only the typed event
 * constants so call sites can't drift into ad-hoc event names.
 */
export function track(event: AnalyticsEvent, props?: Props): void {
  if (!client) return;
  client.capture(event, props);
}

/** Associate the current visitor with a stable user id. NO-OP without a key. */
export function identify(userId: string, props?: Props): void {
  if (!client) return;
  client.identify(userId, props);
}

/** Clear identity on logout. NO-OP without a key. */
export function resetAnalytics(): void {
  if (!client) return;
  client.reset();
}

/** Manual pageview (we disable auto-pageviews). NO-OP without a key. */
export function trackPageview(path?: string): void {
  if (!client) return;
  client.capture("$pageview", path ? { $current_url: path } : undefined);
}
